import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CreateMailboxRequest, MailboxDto } from "@/types/api";

/**
 * Mailbox CRUD against the .NET ContainerProvisioningService. Every call
 * hits 20i under the hood, so failures (auth, package_not_found,
 * already_exists) bubble up as ApiError.code for the UI to act on.
 */

export function useMailboxes(containerId: number | undefined) {
  return useQuery({
    queryKey: ["containers", containerId, "mailboxes"],
    enabled: typeof containerId === "number",
    // The 20i list is the source of truth; let the user refresh manually
    // rather than re-fetching aggressively (each fetch costs a /package
    // scan).
    staleTime: 60_000,
    queryFn: ({ signal }) =>
      api.get<MailboxDto[]>(`/api/containers/${containerId}/mailboxes`, signal)
  });
}

export function useCreateMailbox(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMailboxRequest) =>
      api.post<MailboxDto>(`/api/containers/${containerId}/mailboxes`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["containers", containerId, "mailboxes"] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["containers"] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}

export function useDeleteMailbox(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mailboxId: string) =>
      api.delete(`/api/containers/${containerId}/mailboxes/${encodeURIComponent(mailboxId)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["containers", containerId, "mailboxes"] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["containers"] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}
